import { LiveEventsProvider, UnreadBadge, useLive } from '@/components/LiveEvents';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { AccountDialog } from '@/components/AccountDialog';
import { NotificationBell, StatusMenu } from '@/components/TeamStatus';
import { LayoutDashboard, Users, Upload, Megaphone, Plug, Settings, Menu, LogOut, Zap, Inbox, UsersRound, TrendingUp, ScrollText, BarChart3, PanelLeftClose, PanelLeftOpen, Target, ChevronDown } from 'lucide-react';
import { BrandLogo, BrandMark } from '@/components/BrandLogo';

const nav = [
  { to: '/vc/leads', label: 'Leads', icon: Users },
  { to: '/vc/inbox', label: 'Inbox', icon: Inbox },
  { to: '/vc/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/vc/ads', label: 'Ads', icon: BarChart3 },
  { to: '/vc/performance', label: 'Performance', icon: TrendingUp },
  { to: '/vc/forecast', label: 'Forecast', icon: Target, more: true },
  { to: '/vc/campaigns', label: 'Other spend', icon: Megaphone, more: true },
  { to: '/vc/automation', label: 'Automation', icon: Zap, more: true },
  { to: '/vc/import', label: 'Import', icon: Upload, roles: ['admin'], more: true },
  { to: '/vc/integrations', label: 'Integrations', icon: Plug, more: true },
  { to: '/vc/team', label: 'Team', icon: UsersRound, roles: ['admin'], more: true },
  { to: '/vc/audit', label: 'Audit log', icon: ScrollText, roles: ['admin'], more: true },
  { to: '/vc/settings', label: 'Settings', icon: Settings, more: true },
];
const MORE_KEY = 'verse.sidebar.more';

const initials = (n) => (n || '?').split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
const COLLAPSE_KEY = 'verse.sidebar.collapsed';

const Tip = ({ show, label, children }) => (show ? (
  <Tooltip><TooltipTrigger asChild>{children}</TooltipTrigger><TooltipContent side="right">{label}</TooltipContent></Tooltip>
) : children);

const ToggleBtn = ({ collapsed, onToggle }) => (
  <Tip show label={`${collapsed ? 'Expand' : 'Collapse'} sidebar  [`}>
    <button type="button" onClick={onToggle} data-testid="sidebar-toggle" aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:text-[#1D1D1F] hover:bg-black/5 transition-colors duration-150">
      {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
    </button>
  </Tip>
);

const Brand = ({ collapsed, onToggle }) => (collapsed ? (
  <div className="pt-6 pb-4 flex flex-col items-center gap-2">
    <BrandMark className="h-8 w-8" testid="sidebar-brand-mini" />
    <ToggleBtn collapsed onToggle={onToggle} />
    <NotificationBell />
  </div>
) : (
  <div className="pl-5 pr-3 pt-7 pb-5">
    <div className="flex items-center justify-between gap-1">
      <BrandLogo className="h-11 -ml-0.5" testid="sidebar-brand" />
      <div className="flex items-center shrink-0"><NotificationBell />{onToggle && <ToggleBtn onToggle={onToggle} />}</div>
    </div>
    <div className="mt-1 text-[11px] text-slate-500">Malaysia · longevity &amp; aesthetics</div>
  </div>
));

const SideNav = ({ onNavigate, collapsed = false, onToggle }) => {
  const loc = useLocation();
  const { logout, user } = useAuth();
  const { unread } = useLive();
  const [acct, setAcct] = useState(false);
  const role = user?.role || 'admin';
  const doLogout = () => { logout(); window.location.replace('/vc'); };
  const items = nav.filter((n) => !n.roles || n.roles.includes(role));
  const extra = items.filter((n) => n.more);
  const [moreOpen, setMoreOpen] = useState(() => localStorage.getItem(MORE_KEY) === '1');
  const onMorePage = extra.some((n) => loc.pathname.startsWith(n.to));
  const showMore = moreOpen || onMorePage;
  const toggleMore = () => setMoreOpen(() => { const v = !showMore; localStorage.setItem(MORE_KEY, v ? '1' : '0'); return v; });
  const item = ({ to, label, icon: Icon }) => {
          const active = loc.pathname.startsWith(to);
          const inboxDot = to === '/vc/inbox' && unread.contacts_unread > 0;
          return (
            <Tip key={to} show={collapsed} label={label}>
              <Link to={to} onClick={onNavigate} aria-label={label}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`group relative flex items-center rounded-lg py-2 text-[13px] transition-colors duration-150 ${collapsed ? 'justify-center px-0' : 'gap-3 px-3'} ${active ? 'bg-black/[0.06] text-[#1D1D1F] font-medium' : 'text-slate-600 hover:bg-black/[0.04] hover:text-[#1D1D1F]'}`}
              >
                <Icon className={`h-4 w-4 transition-colors duration-150 ${active ? 'text-[#A8864F]' : 'text-slate-400 group-hover:text-slate-600'}`} />
                {!collapsed && <span className="flex-1">{label}</span>}
                {!collapsed && to === '/vc/inbox' && <UnreadBadge count={unread.contacts_unread} testid="nav-inbox-unread-badge" />}
                {collapsed && inboxDot && <span className="absolute top-1.5 right-3 h-2 w-2 rounded-full bg-[#C7A46A] ring-2 ring-white" data-testid="nav-inbox-unread-dot" />}
              </Link>
            </Tip>
          );
        };
  return (
    <TooltipProvider delayDuration={150}>
    <div className="h-full w-full flex flex-col bg-white/70 backdrop-blur-xl border-r border-black/5 text-[#1D1D1F]" data-testid="sidebar" data-collapsed={collapsed ? 'true' : 'false'}>
      <Brand collapsed={collapsed} onToggle={onToggle} />
      <nav className={`flex-1 space-y-0.5 ${collapsed ? 'px-2' : 'px-3'}`}>
        {items.filter((n) => !n.more).map(item)}
        {extra.length > 0 && (collapsed ? <div className="my-2 mx-3 border-t border-black/5" /> : (
          <button type="button" onClick={toggleMore} disabled={onMorePage} aria-expanded={showMore} data-testid="nav-more-toggle"
            className="w-full flex items-center gap-3 px-3 pt-4 pb-1 text-[11px] font-medium uppercase tracking-wide text-slate-400 enabled:hover:text-slate-600 transition-colors duration-150">
            <span className="flex-1 text-left">More</span>{!onMorePage && <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${showMore ? '' : '-rotate-90'}`} />}
          </button>
        ))}
        {(showMore || collapsed) && <div data-testid="nav-more-group" className="space-y-0.5">{extra.map(item)}</div>}
      </nav>
      {collapsed ? (
        <div className="p-2 pb-4 flex flex-col items-center gap-1.5">
          <Tip show label={user?.name || user?.email || 'Account'}>
            <button onClick={() => setAcct(true)} data-testid="shell-account" aria-label="Account"
              className="h-8 w-8 rounded-full bg-gradient-to-br from-[#E7D7B8] to-[#C7A46A] text-white text-xs font-semibold flex items-center justify-center">{initials(user?.name || user?.email)}</button>
          </Tip>
          <Tip show label="Log out">
            <Button variant="ghost" size="icon" data-testid="shell-logout" onClick={doLogout} aria-label="Log out"
              className="h-8 w-8 text-slate-500 hover:text-[#1D1D1F] hover:bg-black/5"><LogOut className="h-4 w-4" /></Button>
          </Tip>
        </div>
      ) : (<>
        <div className="p-3">
          <div className="flex items-center gap-2.5 rounded-xl bg-black/[0.03] p-2.5">
            <button onClick={() => setAcct(true)} className="flex items-center gap-2.5 min-w-0 flex-1 text-left" data-testid="shell-account">
              <span className="h-8 w-8 shrink-0 rounded-full bg-gradient-to-br from-[#E7D7B8] to-[#C7A46A] text-white text-xs font-semibold flex items-center justify-center">{initials(user?.name || user?.email)}</span>
              <span className="min-w-0">
                <span className="block text-[13px] font-medium truncate">{user?.name || user?.email || 'admin'}</span>
                <span className="block text-[11px] text-slate-500 truncate capitalize" data-testid={role === 'reviewer' ? 'shell-role-reviewer' : 'shell-role'}>{role === 'reviewer' ? 'Meta reviewer · limited access' : role}</span>
              </span>
            </button>
            <Button variant="ghost" size="icon" data-testid="shell-logout" onClick={doLogout}
              className="h-8 w-8 text-slate-500 hover:text-[#1D1D1F] hover:bg-black/5">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {role !== 'reviewer' && <div className="px-5 pb-3 -mt-1"><StatusMenu /></div>}
      </>)}
      <AccountDialog open={acct} onOpenChange={setAcct} user={user} />
    </div>
    </TooltipProvider>
  );
};

const isTyping = (el) => el && (['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName) || el.isContentEditable);

const useCollapsed = () => {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === '1');
  const toggle = React.useCallback(() => setCollapsed((c) => { localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1'); return !c; }), []);
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '[' || e.metaKey || e.ctrlKey || e.altKey || isTyping(document.activeElement)) return;
      e.preventDefault(); toggle();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);
  return [collapsed, toggle];
};

const AppShell = ({ children }) => {
  const { pathname } = useLocation();
  const fullHeight = pathname.startsWith('/vc/inbox');  // the inbox fills the screen; a footer would add a second scroll
  const [mobileOpen, setMobileOpen] = useState(false);
  React.useEffect(() => {
    const close = () => setMobileOpen(false);
    window.addEventListener('verse:close-nav', close);
    return () => window.removeEventListener('verse:close-nav', close);
  }, []);
  const [collapsed, toggle] = useCollapsed();
  return (
    <LiveEventsProvider>
    <div className="min-h-screen flex bg-[#F5F5F7]">
      {/* Desktop sidebar */}
      <aside className={`hidden lg:flex shrink-0 sticky top-0 h-screen transition-[width] duration-200 ease-out ${collapsed ? 'w-16' : 'w-64'}`} data-testid="desktop-sidebar">
        <SideNav collapsed={collapsed} onToggle={toggle} />
      </aside>
      {/* Mobile top header */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header data-testid="mobile-app-bar" className="lg:hidden max-md:[body[data-chat-open]_&]:hidden [@media(max-height:500px)]:[body[data-chat-open]_&]:hidden sticky top-0 z-20 h-14 flex items-center justify-between px-4 bg-white/75 backdrop-blur-xl border-b border-black/5">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="shell-menu"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <SideNav onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
          <BrandLogo className="h-9" testid="mobile-brand" />
          <div className="w-9" />
        </header>
        <main className="flex-1 min-w-0 flex flex-col">
          <div className="flex-1">{children}</div>
          {!fullHeight && <footer className="border-t border-slate-200 px-4 md:px-8 py-4 text-[11px] text-slate-500 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="app-footer">
            <span>© Verse Clinic</span>
            <a href="/privacy-policy" className="hover:underline hover:text-[#1D1D1F]" data-testid="footer-privacy">Privacy Policy</a>
            <a href="/terms-of-service" className="hover:underline hover:text-[#1D1D1F]" data-testid="footer-terms">Terms of Service</a>
            <a href="/data-deletion" className="hover:underline hover:text-[#1D1D1F]" data-testid="footer-data-deletion">Data Deletion</a>
            <a href="/privacy-policy-bm" className="hover:underline hover:text-[#1D1D1F]" data-testid="footer-privacy-bm">Dasar Privasi (BM)</a>
          </footer>}
        </main>
      </div>
    </div>
    </LiveEventsProvider>
  );
};

export default AppShell;
